/* eslint-disable @typescript-eslint/no-explicit-any */
import { FIND_ASSIGNABLE_ROUTINE_ADDONS } from '@gripp/shared-logic';
import { BaseColumnCheckbox, InfiniteDataGrid } from '@gripp/shared-ui';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, Text } from 'react-native';

export type RoutineAddonsAssignListProps = {
  workspaceId: string;
  routineId: string;
  maxHeight?: number;
  keyword?: string;
  assignments: string[];
  updateAssignments: (assignments: string[]) => void;
};

export const RoutineAddonsAssignList = ({
  workspaceId,
  routineId,
  maxHeight,
  keyword,
  assignments,
  updateAssignments,
}: RoutineAddonsAssignListProps) => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<any>(() =>
    workspaceId
      ? { keyword, routineId, assignments: assignments || [], workspaceId }
      : undefined
  );

  const columnHelper = createColumnHelper<any>();
  const columns: ColumnDef<any, any>[] = useMemo(
    () => [
      columnHelper.accessor('selected', {
        cell: BaseColumnCheckbox.checkbox,
        header: BaseColumnCheckbox.checkboxHeader,
        meta: { style: styles.checkboxCell },
      }),
      columnHelper.accessor('name', {
        cell: (info) => (
          <Text style={[styles.cellText, styles.nameLabel]}>
            {info.getValue()}
          </Text>
        ),
        header: () => t('addOn.list.name'),
        meta: { style: styles.cell },
      }),
    ],
    [t]
  );

  useEffect(() => {
    if (!workspaceId) return;
    setFilter({ keyword, routineId, assignments: assignments || [], workspaceId });
  }, [keyword, routineId, workspaceId, assignments]);

  if (!filter) return null;

  return (
    <InfiniteDataGrid
      key={`routine-addons-${(assignments || []).join(',')}`}
      modelName="addon"
      hideResultsLabel={true}
      query={FIND_ASSIGNABLE_ROUTINE_ADDONS}
      filter={filter}
      pageSize={100}
      columns={columns}
      listMaxHeight={maxHeight as number}
      containerStyle={{ height: maxHeight }}
      paddingBottom={0}
      textEmpty={t('addOn.textEmpty')}
      dataPrefix="routine.findAssignableAddonsForRoutine"
      updateRowsSelected={(rows) => updateAssignments(rows.map(row => row.id))}
      isRowSelected={(row) => assignments.includes(row.id)}
      enableScrollRefresh={true}
      showScrollIndicator={true}
    />
  );
};

const styles = StyleSheet.create({
  cell: {
    flex: 1,
    paddingHorizontal: 12,
    paddingVertical: 8,
    justifyContent: 'center',
  },
  checkboxCell: {
    maxWidth: 50,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  cellText: {
    fontSize: 16,
    lineHeight: 17,
    ...Platform.select({
      web: {
        fontWeight: '500',
        flexWrap: 'nowrap',
      },
      default: {
        fontWeight: '400',
        flexWrap: 'wrap',
      },
    }),
  },
  nameLabel: {
    flex: 1,
  },
});
